export const LARGE_IMAGE_DIMENSION_THRESHOLD_PX: number = 900;
export const DEFAULT_IMAGE_PRIORITY: string = 'low';

export enum ImageMetricStatus {
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum ImageComponentName {
  PICTURE_RESPONSE = 'lu-picture-responsive',
  IMG_RESPONSE = 'lu-img-responsive',
}

export const IMAGE_METRIC_ERROR_MESSAGE: string = 'Image load error';

export const normalizeImagePriority: (priority?: string) => string = (priority?: string): string =>
  priority?.toLowerCase() ?? DEFAULT_IMAGE_PRIORITY;

export const getPerformanceNow: () => number = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

export { getImageLoadDurationFromPerformanceAPI } from './performance-api.utils';

export interface ImageElementDimensions {
  naturalWidth?: number;
  naturalHeight?: number;
  displayedWidth?: number;
  displayedHeight?: number;
}

export const resolveImageElementDimensions: (
  target: HTMLImageElement | null | undefined,
  fallbackWidth?: number,
  fallbackHeight?: number
) => ImageElementDimensions = (
  target: HTMLImageElement | null | undefined,
  fallbackWidth?: number,
  fallbackHeight?: number
): ImageElementDimensions => ({
  naturalWidth: target?.naturalWidth,
  naturalHeight: target?.naturalHeight,
  displayedWidth: target?.width ?? fallbackWidth,
  displayedHeight: target?.height ?? fallbackHeight,
});

export const DEFAULT_IMAGE_CATEGORY: string = 'product';
export const IMAGE_CATEGORY_BANNER: string = 'banner';

/**
 * Извлекает размеры изображения из URL по паттерну .../resample/.../WIDTHxHEIGHT/...
 */
export const extractImageDimensionsFromUrl: (url: string) => { width?: number; height?: number } = (
  url: string
): { width?: number; height?: number } => {
  if (!url) {
    return {};
  }

  try {
    // Паттерн: /resample/.../WIDTHxHEIGHT/... или /resample/WIDTHxHEIGHT/...
    const resampleMatch: RegExpMatchArray | null = url.match(/\/resample\/[^/]*\/(\d+)x(\d+)\//);
    if (resampleMatch && resampleMatch.length >= 3) {
      const width: number = parseInt(resampleMatch[1], 10);
      const height: number = parseInt(resampleMatch[2], 10);
      if (!isNaN(width) && !isNaN(height)) {
        return { width, height };
      }
    }

    // Альтернативный паттерн: /resample/WIDTHxHEIGHT/...
    const directMatch: RegExpMatchArray | null = url.match(/\/resample\/(\d+)x(\d+)\//);
    if (directMatch && directMatch.length >= 3) {
      const width: number = parseInt(directMatch[1], 10);
      const height: number = parseInt(directMatch[2], 10);
      if (!isNaN(width) && !isNaN(height)) {
        return { width, height };
      }
    }
  } catch (error) {
    console.warn('[Image Metrics] Failed to extract dimensions from URL:', error);
  }

  return {};
};

/**
 * Определяет категорию изображения на основе размера
 * Если хотя бы одна сторона > 900px, возвращает 'banner', иначе 'product'
 */
export const determineImageCategory: (
  imageCategory?: string,
  width?: number,
  height?: number,
  url?: string
) => string = (imageCategory?: string, width?: number, height?: number, url?: string): string => {
  // Если категория уже указана, используем её
  if (imageCategory) {
    return imageCategory;
  }

  // Проверяем фактические размеры
  if (width !== undefined && height !== undefined) {
    if (width > LARGE_IMAGE_DIMENSION_THRESHOLD_PX || height > LARGE_IMAGE_DIMENSION_THRESHOLD_PX) {
      return IMAGE_CATEGORY_BANNER;
    }
  }

  // Пытаемся извлечь размеры из URL
  if (url) {
    const urlDimensions: { width?: number; height?: number } = extractImageDimensionsFromUrl(url);
    if (urlDimensions.width !== undefined && urlDimensions.height !== undefined) {
      if (
        urlDimensions.width > LARGE_IMAGE_DIMENSION_THRESHOLD_PX ||
        urlDimensions.height > LARGE_IMAGE_DIMENSION_THRESHOLD_PX
      ) {
        return IMAGE_CATEGORY_BANNER;
      }
    }
  }

  return DEFAULT_IMAGE_CATEGORY;
};

/**
 * Нормализует название региона для использования в slug
 * lowercase, пробелы → -, убрать диакритику
 */
export const normalizeRegionSlug: (regionName: string) => string = (regionName: string): string => {
  if (!regionName) {
    return '';
  }

  return regionName
    .toLowerCase()
    .normalize('NFD') // Разделяет символы и диакритические знаки
    .replace(/[\u0300-\u036f]/g, '') // Удаляет диакритические знаки
    .replace(/\s+/g, '-') // Заменяет пробелы на дефисы
    .replace(/[^\w\-]/g, '') // Удаляет все не-буквенно-цифровые символы кроме дефисов
    .replace(/-+/g, '-') // Заменяет множественные дефисы на один
    .replace(/^-|-$/g, ''); // Удаляет дефисы в начале и конце
};
